import { isInrCurrency } from '@/utils/currency';
import { INR_OPC_CIT_UNSUPPORTED_PLAN_TYPES } from '@/common/hardcode';
import { getValidAmountInMinorCurrency } from '@/views/config/coupon/helper/utils';
import { getPreviewEligibility } from '@/apis';
import { logger } from '@/utils/log';

export const PREVIEW_ACTION_TYPES = {
  resetProratedCharges: 'reset/prorated/charges',
  setProratedCharges: 'set/prorated/charges',
  checkPreviewEligibility : 'check/preview/eligibility ',
};

const state = {
  isProratedChargeAmountCalculated: false, // defines if user can continue the preview flow
  proratedChargeAmount: 0,
  proratedChargeTaxAmount: 0,
  proratedChargeTotalAmount: 0,
  creditBalance: 0,
  isLoadingPreview: false,  // determines if "preview" parameter should be added to the submit request
  isPreviewEligible: false, // result of /opc/preview/support_check API call
}

const getters = {
  isInrCitEligible: (state, getters) => {
    const { isValidForPreviewEligibility, isPreviewEligible } = getters;
    return isValidForPreviewEligibility && isPreviewEligible;
  },
  isValidForPreviewEligibility:  (state, getters) => {
    // Front-end validation to check if current plan is eligible for preview
    const { inrCitEnabled, getSelectedPlans, isSelectedCycleSameAsEditablePlansInBought, migratePlanState, curPaymentMethod } = getters;
    if (!isInrCurrency() || !inrCitEnabled || !isSelectedCycleSameAsEditablePlansInBought || getSelectedPlans.length !== 1 || !!migratePlanState.flow || !curPaymentMethod) {
      return false;
    }
    return !getSelectedPlans?.some((item) => INR_OPC_CIT_UNSUPPORTED_PLAN_TYPES.indexOf(item.type) !== -1);
  },
  isProratedChargeAmountCalculated: (state) => state.isProratedChargeAmountCalculated,
  isLoadingPreview: (state) => state.isLoadingPreview,
  isPreviewEligible: (state) => state.isPreviewEligible,
  getProratedChargeAmount: (state) => state.proratedChargeAmount,
  getProratedChargeTaxAmount: (state) => state.proratedChargeTaxAmount,
  getProratedChargeTotalAmount: (state) => ( state.proratedChargeTotalAmount - state.creditBalance),
  getCreditBalance: (state) => state.creditBalance,
}

const mutations = {
  setProratedChargeAmount(state, data = {}) {
    state.proratedChargeAmount = data;
  },
  setProratedChargeTaxAmount(state, data = {}) {
    state.proratedChargeTaxAmount = data;
  },
  setProratedChargeTotalAmount(state, data = {}) {
    state.proratedChargeTotalAmount = data;
  },
  setCreditBalance(state, data = {}){
    state.creditBalance = data;
  },
  setIsProratedChargeAmountCalculated(state, data = {}) {
    state.isProratedChargeAmountCalculated = data;
  },
  setIsLoadingPreview(state, data) {
    state.isLoadingPreview = data;
  },
  setIsPreviewEligible(state, data) {
    state.isPreviewEligible = data;
  }
}

const actions = {
  [PREVIEW_ACTION_TYPES.resetProratedCharges]({ commit }) {
    commit('setProratedChargeAmount', 0);
    commit('setProratedChargeTaxAmount', 0);
    commit('setProratedChargeTotalAmount', 0);
    commit('setCreditBalance', 0);
    commit('setIsProratedChargeAmountCalculated', false);
  },
  [PREVIEW_ACTION_TYPES.setProratedCharges]({ commit }, payload = {}) {
    commit('setProratedChargeAmount', getValidAmountInMinorCurrency(payload.amount));
    commit('setProratedChargeTaxAmount', getValidAmountInMinorCurrency(payload.taxAmount));
    commit('setProratedChargeTotalAmount', getValidAmountInMinorCurrency(payload.totalAmount));
    commit('setCreditBalance', getValidAmountInMinorCurrency(payload.creditBalance || 0 ));
    commit('setIsProratedChargeAmountCalculated', true); // enable "Proceed to Payment" button
  },
  async [PREVIEW_ACTION_TYPES.checkPreviewEligibility]({ getters, commit },  payload = {}) {
    const { isValidForPreviewEligibility, getSelectedPlans, orderDataPayload } = getters;
    if (!isValidForPreviewEligibility) {
      return;
    }

    try {
      const data = payload.flowType === 'add' ?
        getPreviewEligibilityDataForAdd(payload.flowType, getSelectedPlans, orderDataPayload.coupon) :
        getPreviewEligibilityDataForEdit(getters, payload.flowType, getSelectedPlans, orderDataPayload.coupon);
      const { result } = await getPreviewEligibility(data);
      commit('setIsPreviewEligible', result);
    } catch (e) {
      logger.error('get preview eligibility API error', e);
      commit('setIsPreviewEligible', false);
    }
  },
}

const getPreviewEligibilityDataForAdd = (flowType, selectedPlans, coupon) => {
  return {
    flowType: flowType,
    planList: selectedPlans.map(plan => {
      return {
        planCode: plan.plan_code,
        planType: plan.plan_type,
        quantity: plan.cartInfo.selected_number,
      };
    }),
    couponData: {
      code: coupon.code,
      fromCampaignLink: coupon.fromCampaignLink,
      cancelCampaign: coupon.cancelCampaign,
    },
  };
};

const getPreviewEligibilityDataForEdit = (getters, flowType, selectedPlans, coupon) => {
  const { partnerRefer } = getters;
  const updateForm = require('@/flows/EDIT').getUpdateForm(getters);
  return {
    flowType: flowType,
    planList: selectedPlans.map(plan => {
      return {
        oldPlanCode: plan.old_plan_code,
        planCode: plan.plan_code,
        planType: plan.plan_type,
        quantity: plan.cartInfo.selected_number,
      };
    }),
    couponData: {
      code: coupon?.code,
      fromCampaignLink: coupon?.fromCampaignLink,
      cancelCampaign: coupon?.cancelCampaign,
    },
    actionType: updateForm.actionType,
    multiRatePlan: updateForm.multiRatePlan || false,
    extensionData: {
      jointsaleCode: partnerRefer?.jointsaleCode,
      referralCode: partnerRefer?.referralCode,
    },
  };
};

export default {
  state,
  getters,
  mutations,
  actions,
}
